import React, { useState, useMemo, useCallback } from "react";
import { usePermission } from "../../../hooks/usePermission";
import { getLatestFY } from "../../DataManagment/Quantative/component/answerHistoryPage/components/utils";
import SimpleDropdown from "../../../globalComponents/form/dropdowns/SimpleDropdown";
import MultiSelectDropdown from "../../../globalComponents/form/dropdowns/MultiSelectDropdown";
import Spinner from "../../../globalComponents/ui/Spinner";
import ViewOnlyBanner from "../Components/ViewOnlyBanner";
import TagFilterBar from "../Components/TagFilterBar";
import { questionMatchesSteps } from "../Components/TagFilterUtils";
import KpiFrequencyRow from "./KpiFrequencyRow";
import { useKpiFrequencyData } from "./useKpiFrequencyData";
import { useLocations } from "./useLocations";
import { useSaveFrequencies } from "./useSaveFrequencies";
import {
  FREQUENCY_OPTIONS,
  MIXED_VALUE,
  baselineFrequency,
  describeLocations,
  frequencyFilterOptions,
  frequencyLabel,
  fyStatus,
  hasOverride,
  locationBreakdown,
  locationOptions,
  matchesFrequencyFilter,
  resolveFrequency,
  safeParseJSON,
  scopeValue,
  scopesFromValues,
  stageFrequency,
} from "./helpers";
import "./KpiFrequency.css";

/** Full month names, for naming the fiscal start month the cadences anchor to. */
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** How each financial year is tagged in the year picker. */
const FY_STATUS_SUFFIX = { current: " (Current)", future: " (Upcoming)", past: "" };

/**
 * Settings → KPIs → Frequency: sets how often each KPI is reported within a
 * financial year, one KPI at a time or across a selection.
 *
 * Edits stage locally and go out in a single bulk request, so applying a cadence
 * to fifty KPIs is one call rather than fifty.
 * @returns {JSX.Element} the settings page.
 */
export default function KpiFrequencySettings() {
  const [canView] = usePermission("kpi-frequency.view", "KPIS");
  const [canUpdate] = usePermission("kpi-frequency.update", "KPIS");

  /** Financial years come from localStorage once, at mount. */
  const [fyData] = useState(() => safeParseJSON(localStorage.getItem("financialYears"), []));
  const latestFY = useMemo(() => getLatestFY(fyData), [fyData]);
  const [selectedFY, setSelectedFY] = useState(() => latestFY);

  const {
    sources, loading: loadingSites, error: sitesError, reload: reloadSites,
  } = useLocations();

  const siteOptions = useMemo(() => locationOptions(sources), [sources]);


  const [selectedSites, setSelectedSites] = useState([]);

  const scopes = useMemo(() => scopesFromValues(selectedSites), [selectedSites]);
  const hasSites = scopes.length > 0;

  const {
    kpis, savedMap, scopeMaps, baselineMap, defaultFrequency, startMonth,
    loading, refreshing, error, refresh, reload,
  } = useKpiFrequencyData(selectedFY?.id, scopes);

  const selectedSiteLabel = useMemo(
    () => describeLocations(
      siteOptions.filter((option) => selectedSites.includes(option.value))
        .map((option) => option.label),
    ),
    [siteOptions, selectedSites],
  );

  /**
   * Only a year that has already ended is locked; the running year and any the
   * company has opened ahead of time stay editable, so next year's cadences can
   * be set up before it starts.
   */
  const isViewOnly = fyStatus(selectedFY?.financial_year_value, startMonth) === "past";
  const readOnly = isViewOnly || !canUpdate;

  /** Staged edits keyed by KPI id, each a cadence to write at every selected site. */
  const [pending, setPending] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkValue, setBulkValue] = useState("");
  const [search, setSearch] = useState("");
  const [filterSteps, setFilterSteps] = useState([]);
  /** Frequency values the list is narrowed to; empty means show everything. */
  const [frequencyFilter, setFrequencyFilter] = useState([]);
  /** KPIs whose per-location cadences are open, keyed by KPI id. */
  const [expandedIds, setExpandedIds] = useState([]);

  /** What a KPI reports at when no selected site sets a cadence of its own. */
  const rowBaseline = useCallback(
    (kpiId) => baselineFrequency(kpiId, baselineMap, defaultFrequency),
    [baselineMap, defaultFrequency],
  );

  /** The selected sites paired with what each of them configures. */
  const sites = useMemo(
    () => scopes.map((scope, index) => {
      const key = scopeValue(scope);
      return {
        key,
        label: siteOptions.find((option) => option.value === key)?.label ?? `Location ${scope.locationId}`,
        map: scopeMaps[index] || {},
      };
    }),
    [scopes, siteOptions, scopeMaps],
  );

  const handleSaved = useCallback(async () => {
    setPending({});
    setSelectedIds([]);
    setBulkValue("");
    await refresh();
  }, [refresh]);

  const { saving, saveError, lastResult, save, resetAll, clearResult } =
    useSaveFrequencies({ fyId: selectedFY?.id, scopes, onSaved: handleSaved });

  const handleFYChange = (val) => {
    const found = fyData.find((fy) => String(fy.id) === String(val));
    if (!found) return;
    setSelectedFY(found);
    setPending({});
    setSelectedIds([]);
    setBulkValue("");
    setSearch("");
    setFilterSteps([]);
    setFrequencyFilter([]);
    setExpandedIds([]);
    clearResult();
  };

  /**
   * Changing the sites discards staged edits: they were staged against the
   * previous selection's cadences and saving them here would write them to the
   * wrong places.
   */
  const handleSitesChange = (values) => {
    setSelectedSites(values);
    setPending({});
    setSelectedIds([]);
    setBulkValue("");
    setFrequencyFilter([]);
    setExpandedIds([]);
    clearResult();
  };

  const handleRowChange = useCallback((kpiId, value) => {
    setPending((prev) => stageFrequency(prev, [kpiId], value, savedMap, rowBaseline));
  }, [savedMap, rowBaseline]);

  const handleToggleSelect = useCallback((kpiId) => {
    setSelectedIds((prev) =>
      prev.includes(kpiId) ? prev.filter((id) => id !== kpiId) : [...prev, kpiId]);
  }, []);

  const handleToggleBreakdown = useCallback((kpiId) => {
    setExpandedIds((prev) =>
      prev.includes(kpiId) ? prev.filter((id) => id !== kpiId) : [...prev, kpiId]);
  }, []);

  /**
   * The per-location cadences of the rows that are open, and only those: a
   * breakdown of every KPI against every selected site is thousands of entries
   * nobody asked to see.
   */
  const breakdowns = useMemo(() => {
    const open = {};
    for (const kpiId of expandedIds) {
      open[kpiId] = locationBreakdown(
        kpiId, sites, baselineFrequency(kpiId, baselineMap, defaultFrequency),
      );
    }
    return open;
  }, [expandedIds, sites, baselineMap, defaultFrequency]);

  const fyOptions = useMemo(() =>
    fyData.map((fy) => ({
      value: fy.id,
      label: `${fy.financial_year_value}${FY_STATUS_SUFFIX[fyStatus(fy.financial_year_value, startMonth)]}`,
    })),
    [fyData, startMonth],
  );

  /** Stable identity so TagFilterBar's internal counts don't recompute each render. */
  const allKpiIds = useMemo(() => new Set(kpis.map((kpi) => kpi.kpiId)), [kpis]);

  const tagFilteredKpis = useMemo(
    () => kpis.filter((kpi) => questionMatchesSteps(kpi, filterSteps)),
    [kpis, filterSteps],
  );

  const searchedKpis = useMemo(() => {
    if (!search.trim()) return tagFilteredKpis;
    const term = search.toLowerCase();
    return tagFilteredKpis.filter((kpi) =>
      (kpi.title || "").toLowerCase().includes(term) ||
      (kpi.moduleName || "").toLowerCase().includes(term));
  }, [tagFilteredKpis, search]);

  const displayList = useMemo(
    () => searchedKpis.filter((kpi) =>
      matchesFrequencyFilter(
        kpi.kpiId, pending, savedMap, frequencyFilter,
        baselineFrequency(kpi.kpiId, baselineMap, defaultFrequency),
      )),
    [searchedKpis, pending, savedMap, frequencyFilter, baselineMap, defaultFrequency],
  );

  const filterOptions = useMemo(
    () => frequencyFilterOptions(scopes.length > 1),
    [scopes.length],
  );

  const visibleIds = useMemo(() => displayList.map((kpi) => kpi.kpiId), [displayList]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedVisibleCount = useMemo(
    () => visibleIds.filter((id) => selectedSet.has(id)).length,
    [visibleIds, selectedSet],
  );
  const allVisibleSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;
  const pendingCount = Object.keys(pending).length;
  const savedOverrideCount = Object.keys(savedMap).length;

  const handleToggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const handleBulkApply = () => {
    if (!bulkValue || !selectedIds.length) return;
    setPending((prev) => stageFrequency(prev, selectedIds, bulkValue, savedMap, rowBaseline));
    setSelectedIds([]);
    setBulkValue("");
  };

  const handleDiscard = () => {
    setPending({});
    setSelectedIds([]);
    setBulkValue("");
  };

  const handleResetAll = async () => {
    if (!Object.keys(savedMap).length) return;
    const message =
      `Clear the cadences set at ${selectedSiteLabel} in ${selectedFY?.financial_year_value}? ` +
      `Those KPIs go back to the default (${frequencyLabel(defaultFrequency)}); ` +
      `locations outside this selection are untouched.`;
    if (!window.confirm(message)) return;

    setPending({});
    setSelectedIds([]);
    setBulkValue("");
    await resetAll();
  };

  if (!canView) {
    return (
      <div className="kfq-root">
        <div className="kfq-empty">You do not have permission to view KPI frequencies.</div>
      </div>
    );
  }

  if (loadingSites || loading) {
    return (
      <div className="kfq-root">
        <div className="kfq-loading">
          <Spinner size={32} color="var(--accent)" />
          <span>Loading KPI Frequencies…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="kfq-root">
      <div className="kfq-topbar">
        <div className="kfq-topbar-left">
          <div className="kfq-accent" aria-hidden="true" />
          <h1 className="kfq-title">KPI Frequency</h1>
          {refreshing && <Spinner size={14} color="var(--accent)" />}
        </div>

        <div className="kfq-topbar-right">
          {!readOnly && (
            <button
              type="button"
              className="kfq-topbar-btn"
              onClick={handleResetAll}
              disabled={saving || !savedOverrideCount || !hasSites}
              title={`Put every KPI at ${selectedSiteLabel} back on the financial-year default`}
            >
              <i className="fas fa-rotate-left" aria-hidden="true" />
              {`Clear ${scopes.length > 1 ? `${scopes.length} locations` : "this location"}`}
            </button>
          )}
          {siteOptions.length > 0 && (
            <SimpleDropdown
              isMulti
              label="Locations"
              value={selectedSites}
              onChange={handleSitesChange}
              options={siteOptions}
              placeholder="Select locations…"
              searchPlaceholder="Search locations…"
              minWidth={240}
              maxWidth={360}
            />
          )}
          {fyData.length > 0 && (
            <SimpleDropdown
              label="Financial Year"
              value={selectedFY?.id ?? ""}
              onChange={handleFYChange}
              options={fyOptions}
              isAlert={isViewOnly}
            />
          )}
        </div>
      </div>

      <div className="kfq-body">
        {isViewOnly && <ViewOnlyBanner fyValue={selectedFY?.financial_year_value} />}

        {(error || sitesError) && (
          <div className="kfq-banner kfq-banner--error" role="alert">
            <span>
              <i className="fas fa-exclamation-circle" aria-hidden="true" />
              {error || sitesError}
            </span>
            <button
              type="button"
              className="kfq-btn kfq-btn--ghost"
              onClick={error ? reload : reloadSites}
            >
              <i className="fas fa-rotate-right" aria-hidden="true" />Retry
            </button>
          </div>
        )}

        {saveError && (
          <div className="kfq-banner kfq-banner--error" role="alert">
            <span>
              <i className="fas fa-exclamation-circle" aria-hidden="true" />
              {saveError}
            </span>
          </div>
        )}

        {lastResult && (
          <div className="kfq-banner kfq-banner--success" role="status">
            <span>
              <i className="fas fa-check-circle" aria-hidden="true" />
              Saved {lastResult.saved ?? 0} · Reset to default {lastResult.cleared ?? 0}
              {lastResult.skipped?.length ? ` · Skipped ${lastResult.skipped.length}` : ""}
            </span>
            <button
              type="button"
              className="kfq-dismiss-btn"
              onClick={clearResult}
              aria-label="Dismiss"
            >×</button>
          </div>
        )}

        <div className="kfq-banner kfq-banner--info" role="note">
          <span>
            <i className="fas fa-circle-info" aria-hidden="true" />
            {hasSites ? (
              <>
                Periods run from <strong>{MONTH_NAMES[startMonth - 1]}</strong>, the head
                office fiscal start. Editing <strong>{selectedSiteLabel}</strong>
                {scopes.length > 1 && " together — a cadence you pick applies to every one of them"}.
                A KPI nobody has set here reports at the financial-year default,{" "}
                <strong>{frequencyLabel(defaultFrequency)}</strong>.
              </>
            ) : (
              "Pick the locations whose cadences you want to set."
            )}
          </span>
        </div>

        {!hasSites && (
          <div className="kfq-card kfq-empty">
            {siteOptions.length
              ? "Select at least one location to set its KPI cadences."
              : "No locations are set up yet. Add them under Settings → Locations, then set how often each KPI reports there."}
          </div>
        )}

        {hasSites && (
          <div className="kfq-card kfq-filters">
            <div className="kfq-search-wrap">
              <i className="fas fa-search kfq-search-icon" aria-hidden="true" />
              <input
                className="kfq-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by title or module"
                aria-label="Search KPIs by title or module"
              />
            </div>
            <div className="kfq-filter-row">
              <TagFilterBar
                allQuestions={kpis}
                filterSteps={filterSteps}
                onFilterStepsChange={setFilterSteps}
                pendingIds={allKpiIds}
              />
              <div className="kfq-freq-filter">
                <span className="kfq-filter-label">Frequency</span>
                <MultiSelectDropdown
                  options={filterOptions}
                  selected={frequencyFilter}
                  onChange={setFrequencyFilter}
                  placeholder="All frequencies"
                  searchPlaceholder="Search frequency…"
                />
              </div>
            </div>

            <div className="kfq-filter-summary">
              Showing <strong>{displayList.length}</strong> of {kpis.length} KPIs
              {savedOverrideCount > 0 && ` · ${savedOverrideCount} configured`}
              {frequencyFilter.length > 0 && (
                <button
                  type="button"
                  className="kfq-btn kfq-btn--ghost kfq-clear-filter"
                  onClick={() => setFrequencyFilter([])}
                >
                  Clear frequency filter
                </button>
              )}
            </div>
          </div>
        )}

        {hasSites && !readOnly && (
          <div className="kfq-bulkbar">
            <label className="kfq-bulk-select-all">
              <input
                type="checkbox"
                className="kfq-checkbox"
                checked={allVisibleSelected}
                onChange={handleToggleSelectAll}
                disabled={!visibleIds.length}
              />
              {allVisibleSelected ? "Clear selection" : "Select all shown"}
            </label>

            <span className="kfq-bulk-count">{selectedIds.length} selected</span>

            <label className="kfq-visually-hidden" htmlFor="kfq-bulk-frequency">
              Frequency to apply to the selected KPIs
            </label>
            <select
              id="kfq-bulk-frequency"
              className="kfq-select"
              value={bulkValue}
              onChange={(e) => setBulkValue(e.target.value)}
              disabled={!selectedIds.length}
            >
              <option value="">Set frequency…</option>
              {FREQUENCY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>

            <button
              type="button"
              className="kfq-btn kfq-btn--primary"
              onClick={handleBulkApply}
              disabled={!bulkValue || !selectedIds.length}
            >
              Apply to {selectedIds.length || 0}
            </button>

            <div className="kfq-bulkbar-spacer" />

            {pendingCount > 0 && (
              <>
                <span className="kfq-pending-count">{pendingCount} unsaved</span>
                <button
                  type="button"
                  className="kfq-btn kfq-btn--ghost"
                  onClick={handleDiscard}
                  disabled={saving}
                >
                  Discard
                </button>
              </>
            )}
            <button
              type="button"
              className="kfq-btn kfq-btn--primary"
              onClick={() => save(pending)}
              disabled={!pendingCount || saving}
            >
              {saving ? <Spinner size={12} color="var(--btn-primary-text)" /> : <i className="fas fa-check" aria-hidden="true" />}
              Save changes
            </button>
          </div>
        )}

        {hasSites && (
          <div className="kfq-card kfq-list">
            {displayList.length === 0 ? (
              <div className="kfq-empty">No KPIs match the current filters.</div>
            ) : (
              displayList.map((kpi) => (
                <KpiFrequencyRow
                  key={kpi.kpiId}
                  kpiId={kpi.kpiId}
                  title={kpi.title}
                  value={resolveFrequency(kpi.kpiId, pending, savedMap, rowBaseline(kpi.kpiId))}
                  isScoped
                  isOverride={hasOverride(kpi.kpiId, pending, savedMap)}
                  isDirty={Boolean(pending[kpi.kpiId])}
                  isSelected={selectedSet.has(kpi.kpiId)}
                  isMixed={savedMap[kpi.kpiId] === MIXED_VALUE}
                  isExpanded={Boolean(breakdowns[kpi.kpiId])}
                  breakdown={breakdowns[kpi.kpiId] || null}
                  startMonth={startMonth}
                  fyValue={selectedFY?.financial_year_value}
                  disabled={readOnly || saving}
                  onToggleSelect={handleToggleSelect}
                  onToggleBreakdown={handleToggleBreakdown}
                  onChange={handleRowChange}
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
